import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function getColor(count, maxCount) {
  if (count === 0) return "bg-white/[0.03]";
  const ratio = count / Math.max(maxCount, 1);
  if (ratio <= 0.15) return "bg-emerald-900/60";
  if (ratio <= 0.3) return "bg-emerald-800/70";
  if (ratio <= 0.5) return "bg-emerald-700/80";
  if (ratio <= 0.75) return "bg-emerald-600/90";
  return "bg-emerald-500";
}

export default function Heatmap({ data = [] }) {
  const [selectedDay, setSelectedDay] = useState(null);

  const { grid, maxCount, monthLabels } = useMemo(() => {
    const dateMap = {};
    let mx = 0;
    for (const d of data) {
      dateMap[d.date] = d;
      if (d.count > mx) mx = d.count;
    }

    const today = new Date();
    const startDate = new Date(today);
    startDate.setDate(today.getDate() - 364);

    // Align to Monday
    while (startDate.getDay() !== 1) {
      startDate.setDate(startDate.getDate() - 1);
    }

    const weeks = [];
    const monthLbls = [];
    let lastMonth = -1;
    const current = new Date(startDate);

    while (current <= today) {
      const week = [];
      for (let d = 0; d < 7; d++) {
        if (current > today) {
          week.push(null);
        } else {
          const dateStr = current.toISOString().split("T")[0];
          const entry = dateMap[dateStr] || { date: dateStr, count: 0, score: 0, names: [] };
          week.push(entry);

          if (current.getMonth() !== lastMonth) {
            monthLbls.push({ label: MONTHS[current.getMonth()], weekIndex: weeks.length });
            lastMonth = current.getMonth();
          }
        }
        current.setDate(current.getDate() + 1);
      }
      weeks.push(week);
    }

    return { grid: weeks, maxCount: mx, monthLabels: monthLbls };
  }, [data]);

  const totalUnlocked = data.reduce((s, d) => s + d.count, 0);
  const totalScore = data.reduce((s, d) => s + (d.score || 0), 0);
  const activeDays = data.filter((d) => d.count > 0).length;

  return (
    <>
      <Card className="bg-[#111] border-white/5">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-sm font-medium text-white/50">Achievement Heatmap</CardTitle>
            <div className="flex gap-4 text-xs text-white/30">
              <span>{totalUnlocked.toLocaleString()} unlocks</span>
              <span>{totalScore.toLocaleString()} score</span>
              <span>{activeDays} active days</span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <div style={{ minWidth: "max-content" }}>
            {/* Month Labels — aligned to grid columns */}
            <div className="relative mb-1" style={{ paddingLeft: "28px", height: "14px" }}>
              {monthLabels.map((m, i) => (
                <span
                  key={i}
                  className="text-[9px] text-white/20 absolute"
                  style={{ left: `${28 + m.weekIndex * 13}px` }}
                >
                  {m.label}
                </span>
              ))}
            </div>

            {/* Grid */}
            <div className="flex gap-0">
              {/* Day labels */}
              <div className="flex flex-col justify-between mr-1 py-[1px]" style={{ height: `${7 * 13}px` }}>
                {DAYS.map((d, i) => (
                  <span key={i} className="text-[8px] text-white/15 leading-[11px] w-6 text-right">
                    {i % 2 === 0 ? d : ""}
                  </span>
                ))}
              </div>

              {/* Weeks — no inner overflow, shares scroll with month labels */}
              <TooltipProvider delayDuration={100}>
                <div className="flex gap-[2px]">
                  {grid.map((week, wi) => (
                    <div key={wi} className="flex flex-col gap-[2px]">
                      {week.map((day, di) =>
                        day ? (
                          <Tooltip key={di}>
                            <TooltipTrigger asChild>
                              <button
                                className={`w-[11px] h-[11px] rounded-[2px] ${getColor(day.count, maxCount)} hover:ring-1 hover:ring-white/30 transition-all cursor-pointer`}
                                onClick={() => day.count > 0 && setSelectedDay(day)}
                                data-testid={`heatmap-${day.date}`}
                              />
                            </TooltipTrigger>
                            <TooltipContent
                              side="top"
                              className="bg-[#1a1a1a] border-white/10 p-2 max-w-[240px]"
                            >
                              <p className="text-[10px] text-white/40 mb-0.5">
                                {new Date(day.date + "T12:00:00").toLocaleDateString("en-US", {
                                  weekday: "short",
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                })}
                              </p>
                              {day.count > 0 ? (
                                <>
                                  <p className="text-xs text-white font-medium">
                                    {day.count} unlock{day.count !== 1 ? "s" : ""} &middot; {day.score || 0} pts
                                  </p>
                                  {day.names?.length > 0 && (
                                    <div className="mt-1 space-y-0.5">
                                      {day.names.slice(0, 5).map((n, i) => (
                                        <p key={i} className="text-[9px] text-white/50 truncate">{n}</p>
                                      ))}
                                      {(day.names.length > 5 || day.hasMore) && (
                                        <p className="text-[9px] text-white/30 italic">
                                          + more (click to see all)
                                        </p>
                                      )}
                                    </div>
                                  )}
                                </>
                              ) : (
                                <p className="text-xs text-white/30">No unlocks</p>
                              )}
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <div key={di} className="w-[11px] h-[11px]" />
                        )
                      )}
                    </div>
                  ))}
                </div>
              </TooltipProvider>
            </div>
          </div>

          {/* Legend */}
          <div className="flex items-center gap-1 mt-2 justify-end">
            <span className="text-[8px] text-white/20">Less</span>
            {[0, 0.15, 0.3, 0.5, 0.75, 1].map((r, i) => (
              <div
                key={i}
                className={`w-[9px] h-[9px] rounded-[2px] ${getColor(Math.ceil(r * (maxCount || 1)), maxCount)}`}
              />
            ))}
            <span className="text-[8px] text-white/20">More</span>
          </div>
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={!!selectedDay} onOpenChange={() => setSelectedDay(null)}>
        <DialogContent className="bg-[#111] border-white/10 max-w-md max-h-[80vh] overflow-y-auto" data-testid="heatmap-dialog">
          <DialogHeader>
            <DialogTitle className="text-white text-base">
              {selectedDay &&
                new Date(selectedDay.date + "T12:00:00").toLocaleDateString("en-US", {
                  weekday: "long",
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
            </DialogTitle>
            <DialogDescription className="text-white/40 text-xs">
              {selectedDay?.count} unlock{selectedDay?.count !== 1 ? "s" : ""} &middot;{" "}
              {selectedDay?.score || 0} score
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1 mt-2">
            {selectedDay?.names?.map((name, i) => (
              <div key={i} className="flex items-center gap-2 py-1.5 px-2 rounded bg-white/[0.03]">
                <span className="text-white/60 text-[10px] w-4 text-right">{i + 1}</span>
                <span className="text-sm text-white/80">{name}</span>
              </div>
            ))}
            {selectedDay?.hasMore && (
              <p className="text-xs text-white/30 text-center py-2">
                Showing first {selectedDay.names?.length} of {selectedDay.count} achievements
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
